import { useMutation } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';

import ErrorToastContent from 'common/components/ErrorToastContent';
import { usePluralization } from 'common/hooks';
import useUser from 'common/hooks/useUser';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { OfferWithSelectionAndAuthor } from 'modules/shared-menu/types';

import { OfferDraftChanges } from './useOfferDraftChanges';
import { useBatchUpdateOffers } from '../SharedMenuEditorPage/components/Collections/BulkActions/useBatchUpdateOffers';

type OffersMutationProps = {
  offerDraftChanges: OfferDraftChanges;
  onSuccess?: () => void;
};

export const useOffersMutation = () => {
  const { selection, offers } = useSelectionData();
  const { executeBatchUpdateOffers } = useBatchUpdateOffers();
  const { user } = useUser();
  const { FormattedPluralMessage } = usePluralization();

  return useMutation(
    async ({ offerDraftChanges, onSuccess }: OffersMutationProps) => {
      if (!user) {
        throw new Error('User is not authenticated');
      }

      const offerPayloads: OfferWithSelectionAndAuthor[] = offers
        .filter(offer => Boolean(offerDraftChanges[offer.id]))
        .map(({ scope, ...offerPayload }) => ({
          ...offerPayload,
          ...offerDraftChanges[offerPayload.id],
          selection_id: selection.id,
          author: {
            id: user.id,
            kind: 'user',
          },
        }));

      await executeBatchUpdateOffers({
        offerPayloads,
        onSuccess: () => {
          const changedOffersCount = Object.keys(offerDraftChanges).length;
          toast.success(
            <FormattedPluralMessage
              id="shared-menu.editor.image-gallery.add-to-items.update-offers"
              count={changedOffersCount}
            />,
          );
          onSuccess?.();
        },
        onError: errors => {
          toast.error(<ErrorToastContent error={errors[0]} />);
        },
      });
    },
    {
      onError: error => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );
};
